import { Injector } from '@angular/core';
import { pushToStoreArray, Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { cloneDeep, invoke } from '@sama/micro-dash';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { toolNameToShapeType } from '../../../../../cross-team-shared/annotation/shared/abstract-tool';
import { getShapeForEventTracking } from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { KeyboardShortcutService } from '../../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { ShapeGroupOrderState } from '../../../../../team-apis/content/content-connector';
import { EventTrackingValue } from '../../../../../team-apis/core/event-tracking';
import {
  Point2D,
  ShapeLocation,
} from '../../../../../team-apis/otterdam/answers/shape-typings';
import {
  AbstractShapeToolHelper,
  ShapeTool,
} from './abstract-shape-tool-helper';

export interface MultiClickShapeTool extends ShapeTool {
  updateLocationToFollowMouse?(location: ShapeLocation<Point2D>): void;
  handleInProgressMouseDown?(
    helper: MultiClickShapeToolHelper,
    event?: MouseEvent,
  ): void;
}

export class MultiClickShapeToolHelper extends AbstractShapeToolHelper<MultiClickShapeTool> {
  private keyboardShortcutService: KeyboardShortcutService;

  constructor(
    tool: MultiClickShapeTool,
    overlayStore: Store<ShapeGroupOrderState>,
    injector: Injector,
  ) {
    super(tool, overlayStore, injector);
    this.keyboardShortcutService = injector.get(KeyboardShortcutService);
  }

  override startListening() {
    super.startListening();
    const releaseCanvas = this.shellConnector.restrictInteractions('canvas');
    void this.completion.finally(releaseCanvas);

    this.subscribeTo(
      this.store('mouse')('imagePoint').$.pipe(filter(isDefined)),
      this.onMouseMove,
    );
    this.subscribeTo(
      fromEvent<MouseEvent>(document, 'mousedown'),
      this.onMouseDown,
    );
    this.subscribeTo(
      this.keyboardShortcutService.getMatchingEvent$('undoSubpoint'),
      ({ keyboardEvent, eventTrackingValue }) => {
        keyboardEvent.preventDefault(); // prevent browser undo
        this.removeLastPlacedSubpoint(eventTrackingValue);
      },
    );
  }

  override cancel(eventTrackingValue: EventTrackingValue) {
    this.shellConnector.trackEvent({
      id: 'cancel_shape',
      bucket: 'annotation',
      ...eventTrackingValue,
      ...this.getEventContext(),
    });
    super.cancel(eventTrackingValue);
  }

  addPoint(point: Point2D) {
    pushToStoreArray(this.getPointsStore(), point);
  }

  getPointsStore() {
    return this.getKeyLocationStore()('points');
  }

  getKeyLocationStore() {
    return this.inProgressShapeStore('key_locations')(0);
  }

  trackAddInProgressSubpointEvent(
    subpointIndex: number,
    { eventName = 'add_in_progress_subpoint' } = {},
  ) {
    this.shellConnector.trackEvent({
      id: eventName,
      bucket: 'annotation',
      ...this.getEventContext(),
      subpoint_index: subpointIndex,
    });
  }

  getEventContext(): EventTrackingValue {
    return {
      shapes_upon_completion: [
        {
          ...getShapeForEventTracking(this.inProgressShapeStore.state()),
          // A subclass (ClickxToolHelper) may set a different shape type in the
          // store while the shape is in progress (model_input_points). But we
          // want events to use the final type that the shape will have.
          type: toolNameToShapeType(this.tool.name),
          // The completed shape will have this new index
          index: this.shapeIndex,
        },
      ],
    };
  }

  protected removeLastPlacedSubpoint(eventTrackingValue: EventTrackingValue) {
    if (this.getPointsStore().state().length <= 2) {
      this.cancel(eventTrackingValue);
      return;
    }

    this.getPointsStore().mutateUsing((currentPoints) => {
      currentPoints.splice(-2, 1);
    });

    this.shellConnector.trackEvent({
      id: 'undo_subpoint',
      bucket: 'annotation',
      ...eventTrackingValue,
    });
  }

  private onMouseMove() {
    const update = this.tool.updateLocationToFollowMouse;
    if (!update) {
      return;
    }

    this.inProgressShapeStore('key_locations')(0).setUsing((location) => {
      // fix me if you change this code
      // eslint-disable-next-line no-param-reassign -- fix me!
      location = cloneDeep(location);
      update.call(this.tool, location);
      return location;
    });
  }

  private onMouseDown(event: MouseEvent) {
    invoke(this.tool, ['handleInProgressMouseDown'], this, event);
  }
}
